import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Volume2, Loader2, SkipBack, SkipForward, Ear } from "lucide-react";
import axios from "axios";
import { VOICE } from "@/constants/testIds";
import SleepTimer from "@/components/SleepTimer";
import AmbientSounds from "@/components/AmbientSounds";
import MiniPlayerBar from "@/components/MiniPlayerBar";
import useMediaSession, { updateMediaSessionPosition } from "@/hooks/useMediaSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICES = [
  {
    id: "shimmer",
    name: "Luna",
    subtitle: "Gentle Whispers",
    desc: "Soft, dreamy voice perfect for the youngest dreamers",
    color: "from-purple-600 to-violet-400",
    emoji: "🌙",
  },
  {
    id: "onyx",
    name: "Barnaby",
    subtitle: "Warm Grandfather",
    desc: "Deep, comforting resonance that feels like home",
    color: "from-amber-600 to-orange-400",
    emoji: "🦉",
  },
  {
    id: "nova",
    name: "Aria",
    subtitle: "Soothing Motherly",
    desc: "Warm and nurturing, like a loving bedtime lullaby",
    color: "from-rose-500 to-pink-400",
    emoji: "🌸",
  },
  {
    id: "fable",
    name: "Jasper",
    subtitle: "Storybook Adventurer",
    desc: "Expressive and playful with a knack for adventure",
    color: "from-teal-500 to-cyan-400",
    emoji: "🏔️",
  },
];

function WaveformVisualizer({ isPlaying }) {
  const BARS = 28;
  return (
    <div className="flex items-center gap-[3px] h-10">
      {Array.from({ length: BARS }, (_, i) => {
        const baseH = 8 + Math.sin((i / BARS) * Math.PI) * 20 + Math.random() * 8;
        return (
          <motion.div
            key={i}
            className="w-1 rounded-full bg-amber-400/60"
            animate={isPlaying ? {
              height: [baseH, baseH * 0.4 + Math.random() * baseH * 0.8, baseH],
              opacity: [0.6, 1, 0.6],
            } : { height: 4, opacity: 0.3 }}
            transition={{
              duration: 0.6 + Math.random() * 0.4,
              repeat: isPlaying ? Infinity : 0,
              delay: i * 0.04,
              ease: "easeInOut",
            }}
            style={{ height: isPlaying ? undefined : 4 }}
          />
        );
      })}
    </div>
  );
}

export default function VoicePlayer({ storyId, storyTitle, user, onAuthRequired }) {
  const [selectedVoice, setSelectedVoice] = useState("shimmer");
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioId, setAudioId] = useState(null);
  const [isFull, setIsFull] = useState(null);
  const [resumedAt, setResumedAt] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [error, setError] = useState("");
  const [previewVoiceId, setPreviewVoiceId] = useState(null);
  const [previewAudioId, setPreviewAudioId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewPlayingId, setPreviewPlayingId] = useState(null);
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const pollTokenRef = useRef(0);
  const pendingResumeRef = useRef(null);
  const playerCardRef = useRef(null);
  const playerObserverRef = useRef(null);
  const [playerInView, setPlayerInView] = useState(true);

  useEffect(() => () => { pollTokenRef.current++; }, []);

  const setPlayerCardRef = useCallback((el) => {
    playerCardRef.current = el;
    playerObserverRef.current?.disconnect();
    if (!el) return;
    playerObserverRef.current = new IntersectionObserver(
      ([entry]) => setPlayerInView(entry.isIntersecting),
      { threshold: 0, rootMargin: "-96px 0px 0px 0px" }
    );
    playerObserverRef.current.observe(el);
  }, []);

  const handlePreview = async (e, voiceId) => {
    e.stopPropagation();
    if (previewPlayingId === voiceId) {
      previewAudioRef.current?.pause();
      return;
    }
    audioRef.current?.pause();
    if (previewVoiceId === voiceId && previewAudioId) {
      const audio = previewAudioRef.current;
      if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
      return;
    }
    setPreviewLoadingId(voiceId);
    try {
      const { data } = await axios.get(`${API}/voices/preview/${voiceId}`, { withCredentials: true });
      setPreviewVoiceId(voiceId);
      setPreviewAudioId(data.audio_id);
    } catch (err) {
      setError("Couldn't load voice sample. Please try again.");
    } finally {
      setPreviewLoadingId(null);
    }
  };

  useEffect(() => {
    if (previewAudioId && previewAudioRef.current) {
      previewAudioRef.current.play().catch(() => {});
    }
  }, [previewAudioId]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleGenerateNarration = async () => {
    if (!user) { onAuthRequired(); return; }
    if (!storyId) {
      setError("Generate a story first");
      return;
    }
    setIsGenerating(true);
    setError("");
    setAudioId(null);
    setIsPlaying(false);
    const token = ++pollTokenRef.current;
    try {
      const { data } = await axios.post(
        `${API}/stories/${storyId}/narration`,
        { voice_id: selectedVoice },
        { withCredentials: true }
      );
      if (data.status === "ready" && data.audio_id) {
        setAudioId(data.audio_id);
        setIsFull(data.is_full ?? false);
        return;
      }
      // Poll until the narration job completes (full-story TTS can take a few minutes)
      const started = Date.now();
      while (Date.now() - started < 300000) {
        await new Promise((r) => setTimeout(r, 4000));
        if (pollTokenRef.current !== token) return;
        const { data: st } = await axios.get(
          `${API}/stories/${storyId}/narration/status`,
          { params: { voice_id: selectedVoice }, withCredentials: true }
        );
        if (pollTokenRef.current !== token) return;
        if (st.status === "ready" && st.audio_id) {
          setAudioId(st.audio_id);
          setIsFull(st.is_full ?? false);
          return;
        }
        if (st.status === "failed") {
          setError("Narration generation failed. Please try again.");
          return;
        }
      }
      setError("Narration is taking longer than expected. Please try again in a moment.");
    } catch (err) {
      setError(err.response?.data?.detail || "Narration generation failed");
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      previewAudioRef.current?.pause();
      audio.play().catch(console.error);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const newTime = (parseFloat(e.target.value) / 100) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(parseFloat(e.target.value));
  };

  const selectedVoiceData = VOICES.find((v) => v.id === selectedVoice);

  useMediaSession({
    title: audioId ? (storyTitle || "A DreamWeaver Bedtime Story") : null,
    artist: `${selectedVoiceData?.name || "Narrator"} \u00b7 DreamWeaver`,
    isPlaying,
    onPlayPause: audioId ? handlePlayPause : null,
    onSeekBackward: audioId ? () => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15); } : null,
    onSeekForward: audioId ? () => { if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 15); } : null,
  });

  return (
    <>
    <section id="voices" data-testid={VOICE.playerSection} className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 60% 40% at 70% 50%, rgba(109,40,217,0.07) 0%, transparent 60%)"
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="inline-block text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80 mb-4"
          >
            Voice Narration
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Let the story<br />
            <span className="gradient-text">come to life</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-slate-400 max-w-xl mx-auto text-sm"
          >
            Choose a narrator voice and let the bedtime magic unfold with soothing AI-powered audio.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Voice Selection */}
          <motion.div
            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="space-y-3"
          >
            <h3 className="text-sm font-medium text-slate-400 mb-5 tracking-wider uppercase">Choose your narrator</h3>
            {VOICES.map((voice) => (
              <motion.div
                key={voice.id}
                data-testid={`${VOICE.voiceCard}-${voice.id}`}
                role="button"
                tabIndex={0}
                onClick={() => { pollTokenRef.current++; setSelectedVoice(voice.id); setAudioId(null); setIsFull(null); setIsPlaying(false); setIsGenerating(false); }}
                onKeyDown={(e) => { if (e.key === "Enter") { pollTokenRef.current++; setSelectedVoice(voice.id); setAudioId(null); setIsFull(null); setIsPlaying(false); setIsGenerating(false); } }}
                whileHover={{ x: 4 }}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 text-left cursor-pointer ${
                  selectedVoice === voice.id
                    ? "border-amber-400/50 bg-amber-500/10"
                    : "border-purple-500/20 bg-white/3 hover:border-purple-500/40"
                }`}
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${voice.color} flex items-center justify-center text-xl shadow-lg flex-shrink-0`}>
                  {voice.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white text-sm">{voice.name}</span>
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-xs text-slate-400">{voice.subtitle}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{voice.desc}</p>
                </div>
                <button
                  type="button"
                  data-testid={`${VOICE.previewButton}-${voice.id}`}
                  onClick={(e) => handlePreview(e, voice.id)}
                  title={`Hear a sample of ${voice.name}`}
                  aria-label={`Preview ${voice.name}'s voice`}
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    previewPlayingId === voice.id
                      ? "bg-amber-400/25 text-amber-300"
                      : "text-slate-400 hover:text-amber-300 hover:bg-white/10"
                  }`}
                >
                  {previewLoadingId === voice.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : previewPlayingId === voice.id ? (
                    <Pause size={13} />
                  ) : (
                    <Ear size={15} />
                  )}
                </button>
                {selectedVoice === voice.id && (
                  <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                )}
              </motion.div>
            ))}
          </motion.div>

          {/* Player */}
          <motion.div
            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="glass-card rounded-2xl p-8"
          >
            {/* Selected voice header */}
            <div className="flex items-center gap-4 mb-8">
              <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${selectedVoiceData?.color} flex items-center justify-center text-2xl shadow-xl`}>
                {selectedVoiceData?.emoji}
              </div>
              <div>
                <h3 className="font-serif text-xl font-bold text-white">{selectedVoiceData?.name}</h3>
                <p className="text-sm text-slate-400">{selectedVoiceData?.subtitle}</p>
              </div>
            </div>

            {/* Waveform */}
            <div className="mb-6">
              <WaveformVisualizer isPlaying={isPlaying} />
            </div>

            {/* Audio element (hidden) */}
            {audioId && (
              <audio
                ref={audioRef}
                src={`${API}/audio/${audioId}`}
                onTimeUpdate={(e) => {
                  const t = e.target.currentTime;
                  const d = e.target.duration || 0;
                  setCurrentTime(t);
                  setProgress(d ? (t / d) * 100 : 0);
                  updateMediaSessionPosition(d, t);
                  if (d && t > 5 && t < d - 5) {
                    localStorage.setItem(`dw-resume-${audioId}`, String(t));
                  } else if (d && t >= d - 5) {
                    localStorage.removeItem(`dw-resume-${audioId}`);
                  }
                }}
                onLoadedMetadata={(e) => {
                  setDuration(e.target.duration);
                  const saved = parseFloat(localStorage.getItem(`dw-resume-${audioId}`) || "0");
                  if (saved > 5 && saved < e.target.duration - 10) {
                    pendingResumeRef.current = saved;
                    e.target.currentTime = saved;
                  } else {
                    setResumedAt(null);
                  }
                }}
                onSeeked={(e) => {
                  if (pendingResumeRef.current != null) {
                    if (e.target.currentTime > 5) {
                      setResumedAt(pendingResumeRef.current);
                      setCurrentTime(e.target.currentTime);
                      setProgress((e.target.currentTime / (e.target.duration || 1)) * 100);
                    }
                    pendingResumeRef.current = null;
                  }
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => {
                  setIsPlaying(false); setProgress(0); setCurrentTime(0); setResumedAt(null);
                  localStorage.removeItem(`dw-resume-${audioId}`);
                }}
              />
            )}

            {previewAudioId && (
              <audio
                ref={previewAudioRef}
                src={`${API}/audio/${previewAudioId}`}
                onPlay={() => { audioRef.current?.pause(); setPreviewPlayingId(previewVoiceId); }}
                onPause={() => setPreviewPlayingId(null)}
                onEnded={() => setPreviewPlayingId(null)}
              />
            )}

            {/* Progress bar */}
            <div className="mb-4">
              <input
                type="range" min={0} max={100} step={0.1}
                value={progress}
                onChange={handleSeek}
                disabled={!audioId}
                className="audio-progress w-full"
                style={{ background: `linear-gradient(to right, #FBBF24 ${progress}%, rgba(255,255,255,0.15) 0%)` }}
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              {resumedAt != null && (
                <p data-testid="resume-indicator" className="text-center text-xs text-amber-300/80 mt-1.5">
                  Resumed from {formatTime(resumedAt)} — right where you left off
                </p>
              )}
              <SleepTimer audioRef={audioRef} />
              <AmbientSounds active={isPlaying} />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15); }}
                  disabled={!audioId}
                  className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <SkipBack size={16} />
                </button>

                <button
                  data-testid={VOICE.playPauseButton}
                  onClick={handlePlayPause}
                  disabled={!audioId}
                  className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 flex items-center justify-center text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                </button>

                <button
                  onClick={() => { if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 15); }}
                  disabled={!audioId}
                  className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <SkipForward size={16} />
                </button>
              </div>

              {/* Volume */}
              <div className="flex items-center gap-2">
                <Volume2 size={14} className="text-slate-500" />
                <input
                  data-testid={VOICE.volumeSlider}
                  type="range" min={0} max={1} step={0.05}
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="audio-progress w-20"
                  style={{ background: `linear-gradient(to right, rgba(251,191,36,0.7) ${volume * 100}%, rgba(255,255,255,0.15) 0%)` }}
                />
              </div>
            </div>

            {/* Sentinel: mini-player visibility tracks this anchor (near the
                transport controls) instead of the whole tall card, so it
                reliably clears the viewport on short pages too. */}
            <div ref={setPlayerCardRef} className="h-px" />

            {/* Error */}
            {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

            {/* Generate button */}
            <button
              data-testid={VOICE.generateNarrationBtn}
              onClick={handleGenerateNarration}
              disabled={isGenerating || !storyId}
              className="mt-6 w-full py-3 rounded-xl border border-amber-400/30 text-amber-300 text-sm font-medium hover:bg-amber-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <><Loader2 size={14} className="animate-spin" />{user?.is_subscribed ? "Generating full narration (this can take a minute)..." : "Generating narration..."}</>
              ) : (
                <>Generate Narration{!storyId && " (generate a story first)"}</>
              )}
            </button>

            <p className="mt-3 text-center text-xs text-slate-400">
              {audioId !== null && isFull !== null
                ? isFull
                  ? "Full story narration — Premium ✦"
                  : "First chapter preview · Full story narration available for Premium members"
                : user?.is_subscribed
                ? "Full story narration unlocked — Premium ✦"
                : "Narration covers the first chapter preview · Full story available for Premium members"}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
    <AnimatePresence>
      {audioId && !playerInView && (
        <MiniPlayerBar
          title={`${selectedVoiceData?.name || "Narrator"} is narrating`}
          subtitle={`${formatTime(currentTime)} / ${formatTime(duration)}`}
          isPlaying={isPlaying}
          progress={progress}
          onPlayPause={handlePlayPause}
          onSkipBack={() => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15); }}
          onSkipForward={() => { if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 15); }}
          onExpand={() => playerCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
        />
      )}
    </AnimatePresence>
    </>
  );
}
